import { useContext } from "react";
import { Link } from "react-router-dom";
import { AlquileresContext } from "../../../../context/AlquileresContext";
import { formatMonto, diasHastaFecha } from "../../../../utils/rentalCalculations";
import { getContratoEstadoClasses } from "../../../../utils/rentalStatusColors";
import {
  FaFileContract,
  FaUserTie,
  FaUsers,
  FaDollarSign,
  FaExclamationTriangle,
  FaBell,
  FaPlusCircle,
  FaArrowRight,
  FaFileImport,
  FaCog,
} from "react-icons/fa";

const StatCard = ({ count, label, icon: Icon, gradient }) => (
  <div className={`rounded-xl p-5 text-white ${gradient} shadow-md`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-3xl font-bold">{count}</p>
        <p className="text-sm opacity-90 mt-1">{label}</p>
      </div>
      <Icon className="w-10 h-10 opacity-30" />
    </div>
  </div>
);

function AlquileresOverview() {
  const {
    contratos,
    contratosActivos,
    locadores,
    locatarios,
    contratosPorVencer,
    contratosConAumentoProximo,
  } = useContext(AlquileresContext);

  const ingresosARS = contratosActivos
    .filter((c) => c.moneda === "ARS")
    .reduce((sum, c) => sum + (Number(c.comisionMonto) || 0), 0);
  const ingresosUSD = contratosActivos
    .filter((c) => c.moneda === "USD")
    .reduce((sum, c) => sum + (Number(c.comisionMonto) || 0), 0);

  return (
    <div className="px-4 md:px-6 py-4 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Alquileres
        </h1>
        <div className="flex gap-2">
          <Link
            to="/dashboard/alquileres/contratos/nuevo"
            className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            <FaPlusCircle className="w-4 h-4" /> Nuevo Contrato
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          count={contratosActivos.length}
          label="Contratos Activos"
          icon={FaFileContract}
          gradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          count={locadores.length}
          label="Locadores"
          icon={FaUserTie}
          gradient="bg-gradient-to-br from-emerald-500 to-emerald-600"
        />
        <StatCard
          count={locatarios.length}
          label="Locatarios"
          icon={FaUsers}
          gradient="bg-gradient-to-br from-violet-500 to-violet-600"
        />
        <StatCard
          count={
            ingresosARS > 0 && ingresosUSD > 0
              ? `${formatMonto(ingresosARS)} + ${formatMonto(ingresosUSD, "USD")}`
              : ingresosUSD > 0
              ? formatMonto(ingresosUSD, "USD")
              : formatMonto(ingresosARS)
          }
          label="Comisiones Mensuales"
          icon={FaDollarSign}
          gradient="bg-gradient-to-br from-amber-500 to-amber-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Alertas */}
        <div className="space-y-4">
          {/* Contratos por vencer */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <FaExclamationTriangle className="text-amber-500" />
              <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                Contratos por Vencer ({contratosPorVencer.length})
              </h2>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-64 overflow-y-auto">
              {contratosPorVencer.length === 0 ? (
                <p className="px-5 py-4 text-sm text-gray-400">
                  No hay contratos por vencer
                </p>
              ) : (
                contratosPorVencer.map((c) => (
                  <Link
                    key={c.idFirebase}
                    to={`/dashboard/alquileres/contratos/editar/${c.idFirebase}`}
                    className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                        {c.locatarioNombre} - {c.propiedad}
                      </p>
                      <p className="text-xs text-gray-400">
                        Locador: {c.locadorNombre}
                      </p>
                    </div>
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                      {diasHastaFecha(c.fechaVencimiento)} dias
                    </span>
                  </Link>
                ))
              )}
            </div>
          </div>

          {/* Aumentos proximos */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
              <FaBell className="text-blue-500" />
              <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                Aumentos Proximos ({contratosConAumentoProximo.length})
              </h2>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-64 overflow-y-auto">
              {contratosConAumentoProximo.length === 0 ? (
                <p className="px-5 py-4 text-sm text-gray-400">
                  No hay aumentos proximos
                </p>
              ) : (
                contratosConAumentoProximo.map((c) => (
                  <Link
                    key={c.idFirebase}
                    to={`/dashboard/alquileres/aumentos`}
                    className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                        {c.locatarioNombre} - {c.propiedad}
                      </p>
                      <p className="text-xs text-gray-400">
                        Monto actual: {formatMonto(c.montoActual, c.moneda)}
                      </p>
                    </div>
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                      {diasHastaFecha(c.aumento?.proximoAumento)} dias
                    </span>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Acciones Rapidas + Resumen */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5">
            <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Acciones Rapidas
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <Link
                to="/dashboard/alquileres/contratos/nuevo"
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <FaPlusCircle className="text-primary" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Nuevo Contrato
                </span>
              </Link>
              <Link
                to="/dashboard/alquileres/liquidacion"
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <FaDollarSign className="text-green-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Liquidacion
                </span>
              </Link>
              <Link
                to="/dashboard/alquileres/importar"
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <FaFileImport className="text-violet-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Importar Excel
                </span>
              </Link>
              <Link
                to="/dashboard/alquileres/configuracion"
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                <FaCog className="text-gray-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Configuracion
                </span>
              </Link>
            </div>
          </div>

          {/* Resumen por estado */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5">
            <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Contratos por Estado
            </h2>
            <div className="space-y-3">
              {["ACTIVO", "VENCIDO", "RESCINDIDO"].map((estado) => {
                const count = contratos.filter((c) => c.estado === estado).length;
                return (
                  <div key={estado} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getContratoEstadoClasses(estado)}`}>
                        {estado}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      {count}
                    </span>
                  </div>
                );
              })}
              <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
                <span className="text-sm font-medium text-gray-500">Total</span>
                <span className="text-sm font-bold text-gray-800 dark:text-gray-100">
                  {contratos.length}
                </span>
              </div>
            </div>
          </div>

          <Link
            to="/dashboard/alquileres/contratos"
            className="flex items-center justify-center gap-2 w-full p-3 text-sm font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors"
          >
            Ver todos los contratos <FaArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default AlquileresOverview;
